import {useMutation} from '@tanstack/react-query';

import {capitalize} from '@mui/material';
import {TypeLocalNameEnum} from 'domain/usecases';
import {OdontoGuideServicesTypeEnum} from 'presentation/constants';
import {parseOdontoDistrictFilter} from 'presentation/modules/booking/odonto/utils/parseOdontoDistrictFilter';
import {
	MAX_DISTRICT_FILTER_COUNT,
	MIN_DISTANCE_IN_KM,
	MIN_DISTANCE_TO_FETCH_ODONTO_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages';
import {postProvidersDentalGuide} from '../../services/postProvidersDentalGuide';

export function usePostProvidersDentalGuideMutation() {
	const bookingData = useBookingStore(state => state.bookingData);
	const {
		SEARCH_PARAMS: {localityDistance},
	} = useQueryParams();

	const {
		specialtyCode,
		ufCode,
		isManualLocation,
		cityName,
		hasGeoDistrictsFilter,
		coordinates,
		allDistrictsSelected,
		districtName,
		citiesNames,
	} = bookingData;

	const distanceFilterAtMinimumDistance = localityDistance === MIN_DISTANCE_IN_KM;
	const isUsingGeolocation = !isManualLocation;
	const isUsingGeoAndHasDistrictsFilter = isUsingGeolocation && hasGeoDistrictsFilter;
	const isUsingGeoAndHasNoDistrictsFilter = isUsingGeolocation && !hasGeoDistrictsFilter;

	const districtFilter = parseOdontoDistrictFilter({
		districtName,
		citiesNames,
		stateCode: ufCode,
	});

	const shouldSendEmptyDistrictFilter =
		districtFilter?.length > MAX_DISTRICT_FILTER_COUNT || allDistrictsSelected;

	const getDistanceValue = () => {
		if (!isUsingGeolocation || isUsingGeoAndHasDistrictsFilter) {
			return undefined;
		}

		if (distanceFilterAtMinimumDistance || !localityDistance) {
			return MIN_DISTANCE_TO_FETCH_ODONTO_UNITS;
		}

		return localityDistance;
	};

	const tpLocalidade = isUsingGeoAndHasNoDistrictsFilter
		? TypeLocalNameEnum.COORDENADAS
		: TypeLocalNameEnum.CIDADE;

	const filtroBairro = shouldSendEmptyDistrictFilter
		? [
				{
					uf: ufCode?.toUpperCase(),
					cidade: cityName?.toUpperCase(),
					bairro: '',
				},
			]
		: districtFilter;

	const params = {
		cdEspecialidade: specialtyCode,
		dsTipo: OdontoGuideServicesTypeEnum.OUTPATIENT,
		filtroBairro,
		tpLocalidade: capitalize(tpLocalidade.toLowerCase()),
		distancia: getDistanceValue(),
		nmLocal: isUsingGeoAndHasNoDistrictsFilter ? coordinates : undefined,
	};

	return useMutation({
		mutationFn: async ({signal}: {signal?: AbortSignal}) =>
			postProvidersDentalGuide({...params, signal}),
	});
}
